import { FC, useEffect, useRef, RefObject } from 'react';
import { useInView } from 'motion/react';
import Card from '../Card';
import { ProductCarouselItemType } from 'components/home/content/product-carousel.content';

interface CarouselItemProps {
  item: ProductCarouselItemType;
  index: number;
  color: 'seedgreen' | 'snowwhite';
  activeIndex: number | undefined;
  containerRef?: RefObject<HTMLDivElement | null>;
  isSmall?: boolean;
  setVisibleItems?: React.Dispatch<React.SetStateAction<number[]>>;
  onHover: (index: number) => void;
  onClick: (index: number, label: string) => void;
  itemRefs?: RefObject<HTMLDivElement[]>;
  videoRefs: RefObject<any[]>;
  loadVideo?: boolean;
}

const CarouselItem: FC<CarouselItemProps> = ({
  item,
  index,
  color,
  activeIndex,
  containerRef,
  isSmall,
  setVisibleItems,
  onHover,
  onClick,
  itemRefs = { current: [] },
  videoRefs,
  loadVideo,
}) => {
  if (!itemRefs.current) itemRefs.current = [];

  const ref = useRef<HTMLElement>(null);
  const isInView = useInView(ref, {
    initial: false,
    root: containerRef,
    amount: isSmall ? 0.8 : 1, // More forgiving threshold on mobile
  });

  // this use effect is needed to handle carousel mobile view
  // it doesn't run if <ProductGrid> is the parent
  useEffect(() => {
    if (!setVisibleItems) return;
    if (isInView) {
      setVisibleItems((prev) => {
        if (!prev.includes(index)) {
          return [...prev, index].sort((a, b) => a - b);
        }
        return prev;
      });
    } else {
      setVisibleItems((prev) => prev.filter((i) => i !== index));
    }
  }, [isInView, index]);

  return (
    <Card
      key={index}
      index={index}
      color={color}
      active={activeIndex === index}
      ref={(element: HTMLDivElement) => {
        if (element) {
          itemRefs.current[index] = element;
          ref.current = element;
        }
      }}
      onMouseEnter={() => onHover(index)}
      onClick={() => onClick(index, item.label ?? '')}
      videoRefs={videoRefs}
      loadVideo={loadVideo}
      title={item.title}
      label={item.label}
      pills={item.pills}
      media={item.media}
      labelColor={item.labelColor}
      primaryButton={item.primaryButton}
      note={item.note}
      desktopNote={item.desktopNote}
      className={activeIndex === index ? 'in-view' : ''}
      buttonProps={{ animated: true }}
    />
  );
};

export default CarouselItem;
